const INVALID = "Invalid / withheld";
const REDACTED = "[redacted]";
const MISSING = "Not recorded";
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value) &&
	[Object.prototype, null].includes(Object.getPrototypeOf(value));

// Only these fields are displayable. Never traverse arbitrary payloads, before/after
// snapshots, provider requests, credentials, or error objects.
const ID_FIELDS = ["id", "actorUid", "restaurantId", "orderId", "refundId", "stripeRefundId",
	"operationId", "requestId", "recoveryId", "auditId", "sourceId", "jobId",
	"paymentIntentId", "checkoutAttemptId", "partyId", "tableId", "commandId", "printerId",
	"customerId", "caseId", "targetUid", "ownerUid", "firebaseProject", "projectId"];
const CODE_FIELDS = ["action", "actorRole", "actorType", "outcome", "status", "providerStatus",
	"previousStatus", "lastOutcome", "lastFailureCode", "refundType", "sourceType", "intent",
	"deliveryStatus", "tender", "handoverSource", "queue", "role", "environment"];
const TEXT_FIELDS = ["reason", "reviewReason", "resolutionReason"];
const NUMBER_FIELDS = ["amountCents", "previousAttempts", "attempts", "paymentOperations"];
const BOOLEAN_FIELDS = ["success", "changed", "effectsComplete", "handoverConfirmed", "sensorVerified", "replayAllowed", "financialReviewRequired"];
const TIME_FIELDS = ["createdAt", "at", "updatedAt", "reviewedAt", "resolvedAt", "completedAt", "settledAt",
	"previousCreatedAt", "previousClaimedAt", "previousOutcomeAt"];
const DRAWER_REFERENCES = { incidentId: /^di_[a-f0-9]{64}$/, previousCommandId: /^dc_[a-f0-9]{64}$/, nextCommandId: /^dc_[a-f0-9]{64}$/ };
const DRAWER_CODES = { previousKind: ["no_sale", "cash_payment", "cash_refund", "paid_in", "paid_out"],
	previousState: ["queued", "dispatching", "requested", "unknown", "canceled"], physicalOutcome: ["unknown", "staff_reported_open"] };
const TERMINAL_REFERENCES = { readerId: /^tmr_[A-Za-z0-9]{1,124}$/, previousReaderId: /^tmr_[A-Za-z0-9]{1,124}$/,
	locationId: /^tml_[A-Za-z0-9]{1,124}$/, previousLocationId: /^tml_[A-Za-z0-9]{1,124}$/,
	platformAccountId: /^acct_[A-Za-z0-9]{1,123}$/, terminalStripeAccountId: /^acct_[A-Za-z0-9]{1,123}$/,
	payoutConnectedAccountId: /^acct_[A-Za-z0-9]{1,123}$/ };
const TERMINAL_CODES = { terminalAccountScope: ["platform", "connected_account"], previousTerminalAccountScope: ["platform", "connected_account"], mode: ["test", "live"] };
const ID_LIST_FIELDS = ["reviewedOperationIds", "operationIds"];
const DEVICE_FIELDS = ["deviceId", "deviceHash", "previousDeviceHash"];
const sensitiveValue = /(?:(?:bearer|password|passwd|credentials?|secret|token|authorization|pin|otp|iban|cvv|cvc|routing|bank)\b\s*[:=]?\s*\S+|(?:api[_ -]?key|client[_ -]?secret|device[_ -]?token|access[_ -]?token|refresh[_ -]?token|card[_ -]?(?:number|details|last4)|account[_ -]?number)\s*[:=]?\s*\S+|\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]+|_secret_[A-Za-z0-9]+|\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+|\b(?:\d[ -]?){9,}\b|\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b|\bcard\s+(?:ending\s+(?:in\s+)?|last\s*(?:4|four)\s*[:=]?\s*)?\d{4}\b)/i;

function safeString(value, maxLength, pattern) {
	if (value === null) return null;
	if (typeof value !== "string" || !value.trim() || value.length > maxLength) return INVALID;
	if (sensitiveValue.test(value)) return REDACTED;
	if ([...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
		(pattern && !pattern.test(value))) return INVALID;
	return value;
}

export function formatAdminAuditTimestamp(value) {
	if (value === null || value === undefined) return MISSING;
	let milliseconds;
	if (typeof value === "number") {
		milliseconds = value;
	} else if (typeof value === "string") {
		// Serialized audit timestamps are ISO UTC, not locale dates or numeric strings.
		if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value)) return "Unknown / invalid timestamp";
		milliseconds = Date.parse(value);
		if (Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 19) !== value.slice(0, 19)) return "Unknown / invalid timestamp";
	} else if (record(value)) {
		const underscored = own(value, "_seconds");
		const seconds = value[underscored ? "_seconds" : "seconds"];
		const nanos = value[underscored ? "_nanoseconds" : "nanoseconds"];
		const keys = underscored ? ["_seconds", "_nanoseconds"] : ["seconds", "nanoseconds"];
		if (Object.keys(value).some((key) => !keys.includes(key)) || !Number.isSafeInteger(seconds) ||
			!Number.isInteger(nanos) || nanos < 0 || nanos >= 1e9) return "Unknown / invalid timestamp";
		milliseconds = seconds * 1000 + Math.floor(nanos / 1e6);
	}
	return Number.isSafeInteger(milliseconds) && milliseconds >= 0 && milliseconds <= 253402300799999 ?
		new Date(milliseconds).toISOString() : "Unknown / invalid timestamp";
}

function displayFields(source) {
	const output = {};
	const add = (fields, convert) => fields.forEach((key) => {
		if (own(source, key)) output[key] = convert(source[key]);
	});
	add(ID_FIELDS, (value) => safeString(value, 256, /^[A-Za-z0-9_.:@-]+$/));
	Object.entries(DRAWER_REFERENCES).forEach(([key, pattern]) =>
		add([key], (value) => typeof value === "string" && pattern.test(value) ? value : INVALID));
	Object.entries(DRAWER_CODES).forEach(([key, values]) => add([key], (value) => values.includes(value) ? value : INVALID));
	Object.entries(TERMINAL_REFERENCES).forEach(([key, pattern]) =>
		add([key], value => value === null ? null : typeof value === "string" && pattern.test(value) ? value : INVALID));
	Object.entries(TERMINAL_CODES).forEach(([key, values]) => add([key], value => value === null ? null : values.includes(value) ? value : INVALID));
	// These enrollment identifiers are one-way hashes, not the device bearer token.
	add(DEVICE_FIELDS, (value) => typeof value === "string" && /^[a-f0-9]{64}$/.test(value) ? value :
		safeString(value, 256, /^[A-Za-z0-9_.:@-]+$/));
	add(CODE_FIELDS, (value) => safeString(value, 120, /^[A-Za-z0-9_.-]+$/));
	add(TEXT_FIELDS, (value) => safeString(value, 600));
	add(NUMBER_FIELDS, (value) => Number.isSafeInteger(value) && value >= 0 ? value : INVALID);
	add(BOOLEAN_FIELDS, (value) => typeof value === "boolean" ? value : INVALID);
	add(TIME_FIELDS, formatAdminAuditTimestamp);
	add(ID_LIST_FIELDS, (value) => Array.isArray(value) && value.length <= 100 ?
		value.map((id) => safeString(id, 256, /^[A-Za-z0-9_.:@-]+$/)) : INVALID);
	return output;
}

function normalizeAdminAuditLog(log) {
	const topLevel = displayFields(log);
	const payload = log.payload == null ? {} : displayFields(log.payload);
	return {
		id: log.id,
		action: topLevel.action || MISSING,
		actorUid: topLevel.actorUid || MISSING,
		times: ["createdAt", "at"].filter((key) => own(topLevel, key)).map((key) => ({ field: key, value: topLevel[key] })),
		// Payload identities never replace the top-level audit actor or other facts.
		facts: { topLevel, payload },
	};
}

export function normalizeAdminAuditResponse(data) {
	if (!record(data) || !Array.isArray(data.logs) || data.logs.length > 100 ||
		(own(data, "success") && data.success !== true) || data.error != null) throw new Error("Malformed audit response");
	const ids = new Set();
	return data.logs.map((log) => {
		if (!record(log) || typeof log.id !== "string" || !log.id.trim() || log.id.length > 256 || ids.has(log.id) ||
			(log.payload != null && !record(log.payload))) throw new Error("Malformed audit record");
		ids.add(log.id);
		return normalizeAdminAuditLog(log);
	});
}
